import { type ButtonProps, IconButton } from "@hortis/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@hortis/ui/command";
import { CommandMobileHeader } from "@hortis/ui/command/command-menu";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { XClose } from "@hortis/ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@hortis/ui/popover";
import { type FollowUp } from "generated/graphql";
import { useState } from "react";
import { followUpOptions } from "src/components/modals/make-observation/follow-up-options";

export const FollowUpPicker = ({
  value,
  onChange,
  placeholder: _placeholder,
  children,
  ...props
}: {
  value: FollowUp | undefined | null;
  onChange: (val: FollowUp | undefined | null) => void;
  placeholder?: string;
} & Partial<Omit<ButtonProps, "value" | "onChange">>) => {
  const isDesktop = useMediaQuery("md");
  const placeholder = _placeholder ?? "Select a follow-up...";
  const [open, setOpen] = useState(false);

  const handleChange = (followUp: FollowUp | null) => {
    onChange(
      JSON.stringify(value) === JSON.stringify(followUp) ? null : followUp,
    );
    setOpen(false);
  };

  const commandContent = (
    <>
      <CommandEmpty>No results</CommandEmpty>
      <CommandList>
        <CommandGroup>
          {followUpOptions.map((followUp) => (
            <CommandItem
              key={followUp.label}
              onSelect={() => {
                handleChange(followUp.value);
              }}
              data-cy="follow-up-picker-item"
              selected={
                JSON.stringify(value) === JSON.stringify(followUp.value)
              }
            >
              {followUp.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </>
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild data-cy="follow-up-picker" {...props}>
          {children}
        </PopoverTrigger>
        <PopoverContent className="z-99999 min-w-(--radix-popover-trigger-width)!">
          <Command>
            <CommandInput
              placeholder={placeholder}
              data-cy="follow-up-picker-input"
            />
            {commandContent}
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild data-cy="follow-up-picker" {...props}>
        {children}
      </DialogTrigger>
      <DialogContent className="p-0!">
        <Command size="lg">
          <CommandMobileHeader>
            {placeholder}
            <DialogClose asChild>
              <IconButton
                icon={XClose}
                size="xs"
                ariaLabel="Close"
                variant="tertiaryGray"
              />
            </DialogClose>
          </CommandMobileHeader>
          {commandContent}
        </Command>
      </DialogContent>
    </Dialog>
  );
};
